import React from 'react'
import { Box, Fade, CircularProgress, Typography, Button, Alert } from '@mui/material'
import { AutoAwesome, ArrowForward } from '@mui/icons-material'
import SubscriptionPlans from '../components/SubscriptionPlans'
import { MembershipPlan } from '@/types/subscription'

type Props = {
  showSubscriptionDialog: boolean
  onCloseDialog: () => void
  onOpenDialog: () => void
  onPlanSelect: (
    plan: MembershipPlan,
    cycle: 'monthly' | 'yearly',
    amountPaid: string,
    totalPrice: string,
    couponApplied: string
  ) => void | Promise<void>
  isProcessingPayment: boolean
  paymentError: string | null
  onClearPaymentError: () => void
  subscriptionPaymentSuccess: boolean
  onNextAfterPayment: () => void
}

const ChooseSubscriptionStep: React.FC<Props> = ({
  showSubscriptionDialog,
  onCloseDialog,
  onOpenDialog,
  onPlanSelect,
  isProcessingPayment,
  paymentError,
  onClearPaymentError,
  subscriptionPaymentSuccess,
  onNextAfterPayment,
}) => {
  return (
    <Box>
      <SubscriptionPlans open={showSubscriptionDialog} onClose={onCloseDialog} onPlanSelect={onPlanSelect} />

      {isProcessingPayment && (
        <Fade in timeout={800}>
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#7A6FA1', mb: 2 }} size={50} />
            <Typography variant="h6">Processing your subscription...</Typography>
            <Typography variant="body2" sx={{ color: 'grey.600', mt: 1 }}>
              Creating your account and preparing payment
            </Typography>
          </Box>
        </Fade>
      )}

      {!isProcessingPayment && (
        <Fade in timeout={800} style={{ transitionDelay: '400ms' }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            {paymentError && (
              <Fade in timeout={800}>
                <Box sx={{ mb: 4 }}>
                  <Alert severity="error" onClose={onClearPaymentError}>
                    {paymentError}
                  </Alert>
                </Box>
              </Fade>
            )}

            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: 'grey.800',
                mb: 2,
                fontSize: {
                  xs: '1.75rem',
                  sm: '2rem',
                  md: '2.25rem',
                },
              }}
            >
              {subscriptionPaymentSuccess
                ? 'Subscription Completed'
                : 'Choose Your Subscription Plan'}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'grey.600',
                maxWidth: '600px',
                mx: 'auto',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                lineHeight: 1.6,
                mb: 4,
              }}
            >
              {subscriptionPaymentSuccess
                ? 'Your payment is complete. Continue to the next step.'
                : 'Choose a plan that best fits your practice needs.'}
            </Typography>

            {subscriptionPaymentSuccess ? null : !showSubscriptionDialog ? (
              <Fade in timeout={800} style={{ transitionDelay: '600ms' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={onOpenDialog}
                  startIcon={<AutoAwesome />}
                  endIcon={<ArrowForward />}
                  sx={{
                    bgcolor: '#7A6FA1',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0 4px 14px #7A6FA180',
                    '&:hover': {
                      bgcolor: '#7A6FA1',
                      boxShadow: '0 6px 20px #bdb4dfdb',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  Explore Plans & Choose Subscription
                </Button>
              </Fade>
            ) : null}
          </Box>
        </Fade>
      )}
    </Box>
  )
}

export default ChooseSubscriptionStep

